import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getBanks, createBank, updateBank, deleteBank, getCompanies, getCurrencies, getCountries } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingLibraryIcon,
  MagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const Banks = () => {
  usePageTitle('Banks');

  const [banks, setBanks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [viewingBank, setViewingBank] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    bank_name: '',
    account_number: '',
    iban: '',
    swift: '',
    currency: 'EUR',
    bank_address: '',
    bank_country: '',
    company_id: '',
    status: 'active',
  });

  useEffect(() => {
    loadBanks();
    loadLookups();
  }, []);

  const loadBanks = async () => {
    try {
      const response = await getBanks();
      setBanks(response.data);
    } catch (error) {
      toast.error('Error loading bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [companiesRes, currenciesRes, countriesRes] = await Promise.all([
        getCompanies(),
        getCurrencies(),
        getCountries()
      ]);
      setCompanies(companiesRes.data);
      setCurrencies(currenciesRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  const openModal = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({
        name: bank.name || '',
        bank_name: bank.bank_name || '',
        account_number: bank.account_number || '',
        iban: bank.iban || '',
        swift: bank.swift || '',
        currency: bank.currency || 'EUR',
        bank_address: bank.bank_address || '',
        bank_country: bank.bank_country || '',
        company_id: bank.company_id || '',
        status: bank.status || 'active',
      });
    } else {
      setEditingBank(null);
      setFormData({
        name: '',
        bank_name: '',
        account_number: '',
        iban: '',
        swift: '',
        currency: 'EUR',
        bank_address: '',
        bank_country: '',
        company_id: '',
        status: 'active',
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingBank(null);
  };

  const openViewModal = (bank) => {
    setViewingBank(bank);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewingBank(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.bank_name) {
      toast.error('Account name and bank name are required');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        account_number: formData.account_number || null,
        iban: formData.iban || null,
        swift: formData.swift || null,
        bank_address: formData.bank_address || null,
        bank_country: formData.bank_country || null,
        company_id: formData.company_id || null,
      };

      if (editingBank) {
        await updateBank(editingBank.id, submitData);
        toast.success('Bank account updated successfully');
      } else {
        await createBank(submitData);
        toast.success('Bank account created successfully');
      }
      closeModal();
      loadBanks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving bank account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bank) => {
    if (!window.confirm(`Are you sure you want to delete "${bank.name}"?\n\nNote: You cannot delete bank accounts that are used in invoices.`)) {
      return;
    }

    try {
      await deleteBank(bank.id);
      toast.success('Bank account deleted successfully');
      loadBanks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting bank account');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { class: 'badge-approved', label: 'Active' },
      disabled: { class: 'badge-pending', label: 'Disabled' },
      closed: { class: 'badge-rejected', label: 'Closed' },
    };
    const c = config[status] || { class: 'badge-draft', label: status };
    return <span className={`badge ${c.class}`}>{c.label}</span>;
  };

  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  // Filter banks based on search query
  const filteredBanks = banks.filter((bank) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      bank.name?.toLowerCase().includes(searchLower) ||
      bank.bank_name?.toLowerCase().includes(searchLower) ||
      bank.account_number?.toLowerCase().includes(searchLower) ||
      bank.iban?.toLowerCase().includes(searchLower) ||
      bank.currency?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading bank accounts...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bank Accounts</h1>
          <p className="page-subtitle">Manage company bank accounts for payments</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Bank Account
        </button>
      </div>

      <div className="card">
        {banks.length > 0 && (
          <div className="search-bar">
            <div className="search-input-wrapper">
              <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search by name, bank, account number, IBAN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}

        {filteredBanks.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Bank Name</th>
                  <th>Account Number</th>
                  <th>Currency</th>
                  <th>Country</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanks.map((bank) => (
                  <tr key={bank.id}>
                    <td><strong>{bank.name}</strong></td>
                    <td>{bank.bank_name}</td>
                    <td className="font-mono">{bank.account_number || bank.iban || '-'}</td>
                    <td>{bank.currency}</td>
                    <td>{bank.bank_country ? getCountryName(bank.bank_country) : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{getStatusBadge(bank.status)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openViewModal(bank)}
                          title="View Details"
                        >
                          <EyeIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openModal(bank)}
                          title="Edit"
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleDelete(bank)}
                          title="Delete"
                          style={{ color: 'var(--color-accent-danger)' }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : banks.length === 0 ? (
          <div className="empty-state">
            <BuildingLibraryIcon className="empty-state-icon" />
            <p className="empty-state-title">No bank accounts found</p>
            <p>Add your first bank account to get started</p>
            <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Add Bank Account
            </button>
          </div>
        ) : (
          <div className="empty-state">
            <MagnifyingGlassIcon className="empty-state-icon" />
            <p className="empty-state-title">No bank accounts found</p>
            <p>No bank accounts match your search criteria</p>
            <button className="btn btn-secondary" onClick={() => setSearch('')} style={{ marginTop: '1rem' }}>
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* Bank Account Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingBank ? 'Edit Bank Account' : 'Add New Bank Account'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingBank ? 'Update Bank Account' : 'Create Bank Account'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Account Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main EUR Account"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Bank Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="e.g., Deutsche Bank"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input
                type="text"
                className="form-input"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="Bank account number"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select
                className="form-select"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">IBAN</label>
              <input
                type="text"
                className="form-input"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                placeholder="International Bank Account Number"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">SWIFT/BIC</label>
              <input
                type="text"
                className="form-input"
                value={formData.swift}
                onChange={(e) => setFormData({ ...formData, swift: e.target.value.toUpperCase() })}
                placeholder="SWIFT/BIC code"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Bank Address</label>
            <textarea
              className="form-textarea"
              value={formData.bank_address}
              onChange={(e) => setFormData({ ...formData, bank_address: e.target.value })}
              placeholder="Bank branch address"
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Bank Country</label>
              <select
                className="form-select"
                value={formData.bank_country}
                onChange={(e) => setFormData({ ...formData, bank_country: e.target.value })}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Company</label>
              <select
                className="form-select"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
              >
                <option value="">Select company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active - Available for invoice selection</option>
              <option value="disabled">Disabled - Stored but not selectable</option>
              <option value="closed">Closed - Permanently inactive</option>
            </select>
          </div>
        </form>
      </Modal>

      {/* View Bank Account Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        title="Bank Account Details"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeViewModal}>Close</button>
            <button className="btn btn-primary" onClick={() => { closeViewModal(); openModal(viewingBank); }}>
              Edit Bank Account
            </button>
          </>
        }
      >
        {viewingBank && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Account Name</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  <strong>{viewingBank.name}</strong>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bank Name</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.bank_name}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Account Number</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.account_number || '-'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.currency}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">IBAN</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.iban || '-'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">SWIFT/BIC</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.swift || '-'}
                </div>
              </div>
            </div>

            {viewingBank.bank_address && (
              <div className="form-group">
                <label className="form-label">Bank Address</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', whiteSpace: 'pre-wrap' }}>
                  {viewingBank.bank_address}
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Bank Country</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.bank_country ? getCountryName(viewingBank.bank_country) : '-'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingBank.company_name || '-'}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Status</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {getStatusBadge(viewingBank.status)}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Created At</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}>
                  {new Date(viewingBank.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Banks;
